import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { VehicleType } from '../../vehicle/params/vehicle-type';
import { FleetTrailerType } from '../../trailer/models/trailer.model';
import { FleetTrailerService } from '../../trailer/trailer.service';
import { Mobilization, MobilizationStatus } from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';

@Component({
    selector: 'app-mobilization-entries',
    standalone: true,
    templateUrl: './mobilization-entries.component.html',
    styleUrls: ['./mobilization-entries.component.css'],
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class MobilizationEntriesComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    rows: Mobilization[] = [];
    isLoading = false;
    totalRecords = 0;
    first = 0;
    rowsPerPage = 10;
    keyword = '';
    selectedStatus: MobilizationStatus | '' = '';
    dateRange: Date[] | null = null;
    vehicleTypeId?: number;
    trailerTypeId?: number;
    logisticsProjectId?: number;
    vehicleTypes: VehicleType[] = [];
    trailerTypes: FleetTrailerType[] = [];

    readonly statusOptions = [
        { label: 'Draft', value: 'DRAFT' },
        { label: 'Submitted', value: 'SUBMITTED' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' },
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Completed', value: 'COMPLETED' },
        { label: 'Cancelled', value: 'CANCELLED' }
    ];

    constructor(
        private mobilizationService: MobilizationService,
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Mobilization', routerLink: '/fleet/mobilization' }, { label: 'Entries' }];
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes() ?? [];
        this.loadTrailerTypes();
    }

    loadEntries(event?: TableLazyLoadEvent): void {
        if (event) {
            this.first = event.first ?? 0;
            this.rowsPerPage = event.rows ?? 10;
        }

        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isLoading = true;
        this.mobilizationService.searchMobilizations({
            owningCompanyId: companyId,
            page: Math.floor(this.first / this.rowsPerPage),
            pageSize: this.rowsPerPage,
            keyword: this.keyword,
            status: this.selectedStatus,
            startFrom: this.dateRange?.[0],
            startTo: this.dateRange?.[1],
            vehicleTypeId: this.vehicleTypeId,
            trailerTypeId: this.trailerTypeId,
            logisticsProjectId: this.logisticsProjectId
        }).subscribe({
            next: response => {
                this.rows = response.items ?? [];
                this.totalRecords = response.count ?? this.rows.length;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load mobilization entries.' });
            }
        });
    }

    search(): void {
        this.first = 0;
        this.loadEntries();
    }

    clearSearch(): void {
        this.keyword = '';
        this.selectedStatus = '';
        this.dateRange = null;
        this.vehicleTypeId = undefined;
        this.trailerTypeId = undefined;
        this.logisticsProjectId = undefined;
        this.search();
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Records', value: this.totalRecords, hint: 'Matching assignments', tone: 'info' },
            { label: 'Loaded', value: this.rows.length, hint: 'Rows in current page', tone: 'default' },
            { label: 'Active', value: this.rows.filter(row => row.status === 'ACTIVE').length, hint: 'Visible active assignments', tone: 'success' },
            { label: 'Submitted', value: this.rows.filter(row => row.status === 'SUBMITTED').length, hint: 'Visible approvals', tone: 'warn' },
            { label: 'Missing Salary Rule', value: this.rows.filter(row => row.salaryCalculationStatus === 'NO_RULE').length, hint: 'Visible rows without salary rule', tone: 'danger' }
        ];
    }

    openEntry(row: Mobilization): void {
        if (row.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', row.mobilizationId]);
        }
    }

    editEntry(row: Mobilization): void {
        if (row.mobilizationId) {
            this.router.navigate(['/fleet/mobilization/edit', row.mobilizationId]);
        }
    }

    projectLabel(row: Mobilization): string {
        return row.projectSource === 'LOGISTICS_PROJECT'
            ? (row.logisticsProjectId ? `Logistics Project #${row.logisticsProjectId}` : 'Logistics Project')
            : [row.manualProjectCode, row.manualProjectName].filter(Boolean).join(' - ') || 'Manual project';
    }

    siteLabel(row: Mobilization): string {
        return row.manualSiteName || row.manualSiteAddress || 'Site not set';
    }

    trailerLabel(row: Mobilization): string {
        return row.trailerAssetNumber || row.trailerPlateNumber || 'No trailer';
    }

    salaryLabel(row: Mobilization): string {
        if (row.salaryCalculationStatus === 'NO_RULE') {
            return 'No rule';
        }
        if (row.salaryTotalAmount === null || row.salaryTotalAmount === undefined) {
            return '-';
        }
        return `${row.salaryTotalAmount} ${row.salaryCurrencyCode || 'SAR'}`;
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'ACTIVE':
            case 'COMPLETED':
                return 'success';
            case 'SUBMITTED':
            case 'APPROVED':
                return 'warning';
            case 'REJECTED':
            case 'CANCELLED':
                return 'danger';
            case 'DRAFT':
                return 'secondary';
            default:
                return 'info';
        }
    }

    activeFilterCount(): number {
        return [this.keyword, this.selectedStatus, this.dateRange?.[0], this.dateRange?.[1], this.vehicleTypeId, this.trailerTypeId, this.logisticsProjectId].filter(Boolean).length;
    }

    private loadTrailerTypes(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            return;
        }
        this.trailerService.searchTrailerTypes({ owningCompanyId: companyId, page: 0, pageSize: 250, active: true })
            .subscribe({
                next: response => this.trailerTypes = response.items ?? [],
                error: () => this.messageService.add({ severity: 'warn', summary: 'Trailer types', detail: 'Could not load trailer type filters.' })
            });
    }
}
